import React, { useState, useRef, useEffect } from "react";

const UPLOAD_WEBHOOK_URL = process.env.REACT_APP_UPLOAD_WEBHOOK_URL || "https://n8n-7uaK.sliplane.app/webhook/uploadfile";
const LOGO_URL = "https://static.wixstatic.com/media/a7ceca_62524aa5602c421bbbb5c95d73bb58b1~mv2.png";

function PageHeader() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const pageHeaderStyles = {
    background: 'white',
    padding: '1.5rem 2rem',
    borderBottom: '1px solid #e9ecef',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  };

  const headerContainerStyles = {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    direction: 'rtl',
    flexWrap: isMobile ? 'wrap' : 'nowrap'
  };

  const logoPlaceholderStyles = {
    width: '120px',
    height: '60px',
    border: '2px dashed #dee2e6',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6c757d',
    fontSize: '0.9rem',
    backgroundColor: '#f8f9fa',
    flexShrink: '0'
  };

  const pageTitleStyles = {
    fontSize: isMobile ? '1.2rem' : '1.5rem',
    fontWeight: '700',
    color: '#2c3e50',
    margin: '0',
    flex: '1',
    textAlign: 'center',
    minWidth: isMobile ? '100%' : 'auto'
  };

  return (
    <div style={pageHeaderStyles}>
      <div style={headerContainerStyles}>
        <div style={logoPlaceholderStyles}>
          כאן ימוקם הלוגו
        </div>
        <h1 style={pageTitleStyles}>
          ניתוח דוחות שנתיים של חברות הביטוח קרנות הפנסיה והגמל
        </h1>
        <div style={{ width: '120px', flexShrink: '0' }}></div>
      </div>
    </div>
  );
}

export default function App() {
  const [files, setFiles] = useState([]);
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [webhookData, setWebhookData] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      setFiles(prev => [...prev, ...selectedFiles]);
      setUploadError("");
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const wideContainerStyles = {
    maxWidth: '1800px',
    margin: '0 auto',
    width: '100%',
  };

  const simulateProgress = () => {
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 95) {
          clearInterval(interval);
          return 95;
        }
        return prev + Math.random() * 15;
      });
    }, 200);
    return interval;
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      setUploadError("נא לבחור קובץ להעלאה.");
      return;
    }
    setUploading(true);
    setUploadError("");
    const progressInterval = simulateProgress();
    let uploadSucceeded = false;
    try {
      const data = new FormData();
      files.forEach((file, index) => {
        data.append(`file${index}`, file);
      });
      data.append("fullName", fullName);
      data.append("phoneNumber", phoneNumber);

      const res = await fetch(UPLOAD_WEBHOOK_URL, {
        method: "POST",
        body: data,
      });
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const result = await res.json();
      
      // Extract JSON from nested structure (content.parts[0].text)
      let webhookData = result;
      if (result?.content?.parts?.[0]?.text) {
        try {
          const textContent = result.content.parts[0].text;
          // Remove markdown code block markers if present
          const jsonMatch = textContent.match(/```json\s*([\s\S]*?)\s*```/) || textContent.match(/```\s*([\s\S]*?)\s*```/);
          const jsonString = jsonMatch ? jsonMatch[1] : textContent;
          webhookData = JSON.parse(jsonString.trim());
        } catch (parseError) {
          // If parsing fails, try to use the original result
          webhookData = result;
        }
      }
      
      if (webhookData && typeof webhookData === 'object') {
        clearInterval(progressInterval);
        setUploadProgress(100);
        uploadSucceeded = true;
        setTimeout(() => {
          setWebhookData(webhookData);
          setUploadError("");
          setUploading(false);
          setShowResults(true);
        }, 500);
      } else {
        clearInterval(progressInterval);
        setUploadError("העלאת הקובץ נכשלה, נסה שוב.");
        setUploading(false);
      }
    } catch (err) {
      clearInterval(progressInterval);
      setUploadError("שגיאה בהעלאת הקובץ, נסה שוב.");
        setUploading(false);
    }
  };

  const appStyles = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', Arial, sans-serif",
    direction: 'rtl'
  };

  const containerStyles = {
    maxWidth: '800px',
    margin: '1.5rem auto',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box'
  };

  return (
    <div style={appStyles}>
      <PageHeader />

      {!showResults ? (
        <div style={containerStyles}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            border: '1px solid rgba(220, 220, 220, 0.3)'
          }}>
            {!uploading ? (
              <UploadForm 
                files={files}
                fullName={fullName}
                phoneNumber={phoneNumber}
                termsAccepted={termsAccepted}
                showTermsModal={showTermsModal}
                uploadError={uploadError}
                onFileChange={handleFileChange}
                onRemoveFile={removeFile}
                onFullNameChange={(e) => setFullName(e.target.value)}
                onPhoneNumberChange={(e) => setPhoneNumber(e.target.value)}
                onTermsAcceptedChange={(e) => setTermsAccepted(e.target.checked)}
                onShowTermsModal={() => setShowTermsModal(true)}
                onCloseTermsModal={() => setShowTermsModal(false)}
                onSubmit={handleUpload}
              />
            ) : (
              <LoadingScreen progress={uploadProgress} files={files} />
            )}
          </div>
        </div>
      ) : (
        <div style={wideContainerStyles}>
          <ResultsScreen webhookData={webhookData} />
        </div>
      )}
    </div>
  );
}


function UploadForm({ 
  files, 
  fullName, 
  phoneNumber, 
  termsAccepted, 
  showTermsModal,
  uploadError, 
  onFileChange,
  onRemoveFile,
  onFullNameChange, 
  onPhoneNumberChange, 
  onTermsAcceptedChange,
  onShowTermsModal,
  onCloseTermsModal,
  onSubmit 
}) {
  const validatePhone = (phone) => {
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

  const baseInfoValid = fullName.trim() && validatePhone(phoneNumber) && termsAccepted;
  const canSelectFile = !!baseInfoValid;
  const canSubmit = !!(baseInfoValid && files.length > 0);

  const formStyles = {
    padding: '2rem 1.5rem'
  };

  const headerStyles = {
    textAlign: 'center',
    marginBottom: '1.5rem'
  };

  const iconWrapperStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '60px',
    height: '60px',
    background: 'linear-gradient(135deg, #dc3545, #c82333)',
    borderRadius: '50%',
    color: 'white',
    marginBottom: '1rem',
    boxShadow: '0 4px 20px rgba(220, 53, 69, 0.3)'
  };

  const titleStyles = {
    fontSize: '1.8rem',
    color: '#2c3e50',
    marginBottom: '0.5rem',
    fontWeight: '600',
    margin: '0 0 0.5rem 0'
  };

  const descriptionStyles = {
    color: '#6c757d',
    fontSize: '1rem',
    margin: '0'
  };

  const inputSectionStyles = {
    marginBottom: '1.5rem'
  };

  const inputLabelStyles = {
    display: 'block',
    fontWeight: '500',
    color: '#495057',
    marginBottom: '0.5rem',
    fontSize: '0.95rem'
  };

  const inputStyles = {
    width: '100%',
    padding: '0.875rem 1rem',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    fontSize: '1rem',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    background: '#fff',
    boxSizing: 'border-box',
    direction: 'rtl'
  };

  const uploadLabelStyles = {
    display: 'block',
    border: '2px dashed #dee2e6',
    borderRadius: '8px',
    padding: '2.5rem 2rem',
    cursor: canSelectFile ? 'pointer' : 'not-allowed',
    transition: 'all 0.3s ease',
    background: canSelectFile ? '#f8f9fa' : '#e9ecef',
    marginBottom: '2rem',
    textAlign: 'center',
    position: 'relative',
    opacity: canSelectFile ? 1 : 0.6
  };

  const uploadContentStyles = {
    color: '#495057'
  };

  const uploadTextStyles = {
    display: 'block',
    fontSize: '1.1rem',
    fontWeight: '500',
    marginBottom: '0.5rem'
  };

  const disabledOverlayStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    fontSize: '0.9rem',
    color: '#6c757d',
    fontWeight: '500'
  };

  const termsSectionStyles = {
    marginBottom: '2rem',
    padding: '1rem',
    background: '#f8f9fa',
    borderRadius: '6px',
    border: '1px solid #dee2e6'
  };

  const checkboxContainerStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    direction: 'rtl'
  };

  const checkboxStyles = {
    width: '18px',
    height: '18px',
    marginTop: '2px',
    cursor: 'pointer',
    flexShrink: '0'
  };

  const termsTextStyles = {
    fontSize: '0.95rem',
    color: '#495057',
    lineHeight: '1.6',
    flex: '1'
  };

  const termsLinkStyles = {
    color: '#dc3545',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: '500'
  };

  const buttonStyles = {
    width: '100%',
    background: canSubmit ? 'linear-gradient(135deg, #dc3545, #c82333)' : '#e9ecef',
    color: canSubmit ? 'white' : '#6c757d',
    border: 'none',
    padding: '1rem 2rem',
    borderRadius: '6px',
    fontSize: '1.1rem',
    fontWeight: '500',
    cursor: canSubmit ? 'pointer' : 'not-allowed',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    boxShadow: canSubmit ? '0 2px 8px rgba(220, 53, 69, 0.2)' : 'none'
  };

  const errorStyles = {
    marginTop: '1rem',
    padding: '0.875rem 1rem',
    background: '#f8d7da',
    color: '#721c24',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    border: '1px solid #f5c6cb'
  };

  return (
    <>
      <form style={formStyles} onSubmit={onSubmit}>
        <div style={headerStyles}>
          <div style={iconWrapperStyles}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10,9 9,9 8,9"/>
            </svg>
          </div>
          <h2 style={titleStyles}>העלאת דוח שנתי/רבעוני</h2>
          <p style={descriptionStyles}>בחר את הדוח שברצונך לנתח</p>
        </div>

        <div style={inputSectionStyles}>
          <label style={inputLabelStyles}>שם מלא *</label>
          <input
            type="text"
            placeholder="הכנס שם מלא"
            value={fullName}
            onChange={onFullNameChange}
            dir="rtl"
            style={inputStyles}
            onFocus={(e) => {
              e.target.style.borderColor = '#dc3545';
              e.target.style.boxShadow = '0 0 0 0.2rem rgba(220, 53, 69, 0.25)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#ced4da';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={inputSectionStyles}>
          <label style={inputLabelStyles}>מספר טלפון *</label>
          <input
            type="tel"
            placeholder="הכנס מספר טלפון (10 ספרות)"
            value={phoneNumber}
            onChange={onPhoneNumberChange}
            dir="rtl"
            style={inputStyles}
            maxLength="10"
            onFocus={(e) => {
              e.target.style.borderColor = '#dc3545';
              e.target.style.boxShadow = '0 0 0 0.2rem rgba(220, 53, 69, 0.25)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#ced4da';
              e.target.style.boxShadow = 'none';
            }}
          />
          {phoneNumber && !validatePhone(phoneNumber) && (
            <div style={{ fontSize: '0.85rem', color: '#dc3545', marginTop: '0.5rem' }}>
              מספר טלפון חייב להכיל 10 ספרות
            </div>
          )}
        </div>

        <div style={termsSectionStyles}>
          <div style={checkboxContainerStyles}>
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={onTermsAcceptedChange}
              style={checkboxStyles}
            />
            <div style={termsTextStyles}>
              אני מסכים/ה ל
              <span 
                onClick={onShowTermsModal}
                style={termsLinkStyles}
              >
                {' '}תנאי השימוש
              </span>
              {' '}*
            </div>
          </div>
        </div>

        <label 
          style={uploadLabelStyles}
          onMouseOver={(e) => {
            if (canSelectFile) {
              e.target.style.borderColor = '#dc3545';
              e.target.style.background = '#fff5f5';
            }
          }}
          onMouseOut={(e) => {
            if (canSelectFile) {
              e.target.style.borderColor = '#dee2e6';
              e.target.style.background = '#f8f9fa';
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (canSelectFile) {
              const files = e.dataTransfer.files;
              if (files.length > 0) {
                const f = files[0];
                const fakeEvent = {
                  target: {
                    files: [f]
                  }
                };
                onFileChange(fakeEvent);
              }
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (canSelectFile) {
              e.target.style.borderColor = '#dc3545';
              e.target.style.background = '#fff5f5';
            }
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            if (canSelectFile) {
              e.target.style.borderColor = '#dee2e6';
              e.target.style.background = '#f8f9fa';
            }
          }}
        >
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,image/*"
            onChange={onFileChange}
            disabled={!canSelectFile}
            multiple
            style={{ display: 'none' }}
            id="file-upload-input"
          />
          <div style={uploadContentStyles}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="1.5" style={{ marginBottom: '1rem' }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17,8 12,3 7,8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <span style={uploadTextStyles}>
              {files.length > 0 ? `נבחרו ${files.length} קבצים` : "לחץ כאן או גרור קבצים (PDF או תמונות)"}
            </span>
          </div>
          {!canSelectFile && (
            <div style={disabledOverlayStyles}>
              נא למלא את כל השדות ולאשר את תנאי השימוש
            </div>
          )}
        </label>

        {/* רשימת קבצים שנבחרו */}
        {files.length > 0 && (
          <div style={{
            marginTop: '1rem',
            marginBottom: '1rem'
          }}>
            {files.map((file, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.75rem 1rem',
                background: '#f8f9fa',
                borderRadius: '6px',
                marginBottom: '0.5rem',
                direction: 'rtl'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flex: 1,
                  minWidth: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#495057" strokeWidth="2">
                    {file.type.startsWith('image/') ? (
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    ) : (
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    )}
                  </svg>
                  <span style={{
                    fontSize: '0.9rem',
                    color: '#495057',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1
                  }}>
                    {file.name}
                  </span>
                  <span style={{
                    fontSize: '0.85rem',
                    color: '#6c757d',
                    marginRight: '0.5rem'
                  }}>
                    ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveFile(index)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#dc3545',
                    cursor: 'pointer',
                    padding: '0.25rem 0.5rem',
                    marginRight: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="הסר קובץ"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}


        <button 
          type="submit" 
          disabled={!canSubmit} 
          style={buttonStyles}
          onMouseOver={(e) => {
            if (canSubmit) {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.3)';
            }
          }}
          onMouseOut={(e) => {
            if (canSubmit) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(220, 53, 69, 0.2)';
            }
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
          </svg>
          שלח
        </button>

        {uploadError && (
          <div style={errorStyles}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            {uploadError}
          </div>
        )}
      </form>

      {showTermsModal && <TermsModal onClose={onCloseTermsModal} />}
    </>
  );
}

function TermsModal({ onClose }) {
  const modalOverlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem'
  };

  const modalContentStyles = {
    background: 'white',
    borderRadius: '12px',
    padding: '2rem',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '80vh',
    overflowY: 'auto',
    direction: 'rtl',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
    position: 'relative'
  };

  const closeButtonStyles = {
    position: 'absolute',
    top: '1rem',
    left: '1rem',
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '1.2rem',
    color: '#495057'
  };

  const titleStyles = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#2c3e50',
    marginBottom: '1.5rem',
    textAlign: 'center'
  };

  const contentStyles = {
    fontSize: '1rem',
    lineHeight: '1.8',
    color: '#495057'
  };

  const termsText = `תנאי שימוש בשירות ניתוח דוחות פנסיוניים
אנו מספקים שירות לפענוח וניתוח דוחות פנסיוניים לצרכים אינפורמטיביים בלבד.
השירות אינו מהווה ייעוץ פיננסי, השקעות או ייעוץ פנסיוני מחייב. הנתונים וההמלצות המוצגים הם לשם הבנה כללית בלבד.
השימוש בשירות הוא באחריות המשתמש בלבד. אין להסתמך על המידע ללא התייעצות עם סוכן פנסיוני מורשה.
המשתמש מאשר כי כל המידע שהוזן הינו נכון ומדויק, ויחתור להכניס פרטים עדכניים בלבד.
אין אנו אחראים לנזקים הנגרמים כתוצאה משימוש במידע שגוי, חסר או פרשנות לא נכונה של הדוח.
המשתמש מעניק הסכמה לשימוש בדוח הפנסיוני שלו לצרכי ניתוח והפקת תובנות במסגרת השירות בלבד.
שמירה ועמידה בתנאי הפרטיות ואבטחת מידע הם מחויבותנו ואנו נעשה כל שנדרש לצורך הגנת המידע שלך.`;

  return (
    <div style={modalOverlayStyles} onClick={onClose}>
      <div style={modalContentStyles} onClick={(e) => e.stopPropagation()}>
        <button style={closeButtonStyles} onClick={onClose}>
          ×
        </button>
        <h2 style={titleStyles}>תנאי שימוש</h2>
        <div style={contentStyles}>
          {termsText.split('\n').filter(line => line.trim()).map((line, index) => (
            <p key={index} style={{ margin: '0 0 0.5rem 0' }}>
              {line.trim()}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

function LoadingScreen({ progress, files }) {
  const [dots, setDots] = useState("");

  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? "" : prev + ".");
    }, 500);

    return () => {
      clearInterval(dotsInterval);
    };
  }, []);

  const containerStyles = {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '700px',
    margin: '0 auto',
    border: '1px solid rgba(220, 220, 220, 0.3)'
  };

  const contentStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem'
  };

  const titleStyles = {
    fontSize: '1.6rem',
    color: '#2c3e50',
    margin: '0',
    fontWeight: '600'
  };

  const fileNameStyles = {
    color: '#dc3545',
    fontWeight: '500',
    fontSize: '1rem',
    background: '#fff5f5',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    margin: '0',
    border: '1px solid rgba(220, 53, 69, 0.2)'
  };

  const progressContainerStyles = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    maxWidth: '400px'
  };

  const progressBarStyles = {
    flex: '1',
    height: '8px',
    background: '#e9ecef',
    borderRadius: '4px',
    overflow: 'hidden'
  };

  const progressFillStyles = {
    height: '100%',
    background: 'linear-gradient(90deg, #dc3545, #c82333)',
    transition: 'width 0.3s ease',
    borderRadius: '4px',
    width: `${progress}%`
  };

  const progressTextStyles = {
    fontWeight: '600',
    color: '#495057',
    minWidth: '50px',
    fontSize: '0.95rem'
  };


  return (
    <div style={containerStyles}>
      <div style={contentStyles}>
        <div style={{ position: 'relative' }}>
          <svg 
            width="80" 
            height="80" 
            viewBox="0 0 50 50"
            style={{
              animation: 'rotate 2s linear infinite'
            }}
          >
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="#dc3545"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDashoffset="0"
              strokeDasharray="31.416"
              style={{
                animation: 'dash 1.5s ease-in-out infinite'
              }}
            />
          </svg>
        </div>

        <h2 style={titleStyles}>מעבד את המסמך שלך{dots}</h2>
        
        {files && files.length > 0 && (
          <div style={fileNameStyles}>
            {files.length === 1 ? (
              <>📄 {files[0].name}</>
            ) : (
              <>📄 {files.length} קבצים</>
            )}
          </div>
        )}

        <div style={progressContainerStyles}>
          <div style={progressBarStyles}>
            <div style={progressFillStyles} />
          </div>
          <span style={progressTextStyles}>{Math.round(progress)}%</span>
        </div>
      </div>

      <style>
        {`
          @keyframes rotate {
            100% {
              transform: rotate(360deg);
            }
          }

          @keyframes dash {
            0% {
              stroke-dasharray: 1, 150;
              stroke-dashoffset: 0;
            }
            50% {
              stroke-dasharray: 90, 150;
              stroke-dashoffset: -35;
            }
            100% {
              stroke-dasharray: 90, 150;
              stroke-dashoffset: -124;
            }
          }
        `}
      </style>
    </div>
  );
}

function ResultsScreen({ webhookData }) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      // נקודת שבירה ב-1024px
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // פונקציה לעיצוב מספרים - כסף ואחוזים
  const formatNumber = (key, value) => {
    if (value === null || value === undefined) return 'לא זמין';
    if (typeof value !== 'number') return value;
    
    const keyLower = key.toLowerCase();
    const keyHebrew = key;
    
    // בדיקה אם זה אחוז
    const isPercentage = keyLower.includes('percent') || 
                        keyLower.includes('אחוז') || 
                        keyLower.includes('%') ||
                        keyLower.includes('fee') ||
                        keyHebrew.includes('אחוז') ||
                        keyHebrew.includes('דמי ניהול') ||
                        (keyLower.includes('return') && value < 100) ||
                        (keyHebrew.includes('תשואה') && value < 100);
    
    // בדיקה אם זה כסף
    const isMoney = keyLower.includes('balance') || 
                   keyLower.includes('amount') || 
                   keyLower.includes('sum') || 
                   keyLower.includes('cost') ||
                   keyLower.includes('pension') ||
                   keyLower.includes('coverage') ||
                   keyLower.includes('deposit') ||
                   keyHebrew.includes('יתרה') ||
                   keyHebrew.includes('קצבה') ||
                   keyHebrew.includes('כיסוי') ||
                   keyHebrew.includes('עלות') ||
                   keyHebrew.includes('פקדות') ||
                   keyHebrew.includes('שקלים') ||
                   (value > 100 && !isPercentage);
    
    if (isPercentage) {
      return `${value}%`;
    } else if (isMoney) {
      return `₪${value.toLocaleString('he-IL')}`;
    } else {
      return value.toLocaleString('he-IL');
    }
  };

  // פונקציה משופרת להמרת טקסט לHTML
  const formatMarkdown = (text) => {
    if (!text) return '';
    
    return text
      // ניקוי תחילי
      .trim()
      
      // זיהוי כותרות מיוחדות (עם נקודתיים)
      .replace(/^([^:\n]+):/gm, '<h3 style="color: #dc3545; font-size: 1.2rem; font-weight: 600; margin: 1.5rem 0 1rem 0; border-bottom: 1px solid #dc3545; padding-bottom: 0.5rem;">$1</h3>')
      
      // כותרות רגילות
      .replace(/^# (.*$)/gm, '<h1 style="color: #dc3545; font-size: 1.5rem; font-weight: 700; margin: 2rem 0 1rem 0; border-bottom: 2px solid #dc3545; padding-bottom: 0.5rem;">$1</h1>')
      .replace(/^## (.*$)/gm, '<h2 style="color: #2c3e50; font-size: 1.3rem; font-weight: 600; margin: 1.5rem 0 1rem 0;">$1</h2>')
      .replace(/^### (.*$)/gm, '<h3 style="color: #495057; font-size: 1.1rem; font-weight: 600; margin: 1rem 0 0.5rem 0;">$1</h3>')
      
      // זיהוי שמות חברות (בתחילת שורה)
      .replace(/^(הראל|מיטב דש|אנליסט|מגדל|מור פנסיה)/gm, '<strong style="color: #dc3545; font-weight: 700;">$1</strong>')
      
      // זיהוי מספרי תוכניות וסכומים
      .replace(/(תוכנית מספר\s+[\d\-]+)/g, '<span style="background: #f8f9fa; padding: 0.2rem 0.5rem; border-radius: 4px; font-family: monospace; color: #495057;">$1</span>')
      .replace(/(₪\s*[\d,]+)/g, '<strong style="color: #28a745; font-weight: 600;">$1</strong>')
      
      // טקסט מודגש ונטוי
      .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600; color: #2c3e50;">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em style="color: #6c757d;">$1</em>')
      
      // רשימות ממוספרות
      .replace(/^(\d+)\.\s+(.*$)/gm, '<div style="margin: 0.75rem 0; padding-right: 2rem; position: relative; color: #495057; line-height: 1.6;"><span style="position: absolute; right: 0; color: #dc3545; font-weight: bold; width: 1.5rem;">$1.</span>$2</div>')
      
      // רשימות עם נקודות
      .replace(/^[-*]\s+(.*$)/gm, '<div style="margin: 0.5rem 0; padding-right: 1.5rem; position: relative; color: #495057; line-height: 1.6;"><span style="position: absolute; right: 0; color: #dc3545; font-weight: bold;">•</span> $1</div>')
      
      // זיהוי דירוגים ומקומות
      .replace(/(מקום\s+\d+|דירוג\s+\d+|דורג\s+במקום\s+\d+)/g, '<span style="background: #fff3cd; color: #856404; padding: 0.2rem 0.4rem; border-radius: 3px; font-weight: 500;">$1</span>')
      
      // זיהוי אחוזי תשואה
      .replace(/(\d+\.\d+%)/g, '<span style="background: #d1ecf1; color: #0c5460; padding: 0.2rem 0.4rem; border-radius: 3px; font-weight: 500;">$1</span>')
      
      // קוד
      .replace(/`([^`]+)`/g, '<code style="background: #f8f9fa; color: #dc3545; padding: 0.25rem 0.5rem; border-radius: 4px; font-family: monospace; font-size: 0.9rem; border: 1px solid #dee2e6;">$1</code>')
      
      // מעברי שורות - פתרון הבעיה העיקרית
      .replace(/\n\s*\n\s*\n/g, '<div style="margin: 2rem 0;"></div>') // 3+ שורות ריקות = מרווח גדול
      .replace(/\n\s*\n/g, '<div style="margin: 1rem 0;"></div>') // 2 שורות ריקות = פסקה חדשה
      .replace(/\n/g, '<br>'); // שורה בודדת = מעבר שורה
  };

  const containerStyles = {
    background: 'white',
    minHeight: '100vh',
    width: '100%'
  };

  // עיצוב למסכים גדולים - מיכל מרכזי עם רוחב מוגבל
  const contentStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    padding: '16px',
    maxWidth: '800px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box'
  };

  const analysisContentStyles = {
    background: 'transparent',
    border: 'none',
    borderRadius: '0',
    padding: '0',
    lineHeight: '1.6',
    fontSize: '1rem',
    color: '#495057',
    boxShadow: 'none',
    direction: 'rtl',
    width: '100%'
  };



  // עיצוב למובייל - אחד מתחת לשני ברוחב מלא
  const mobileContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    padding: '16px',
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
    boxSizing: 'border-box'
  };

  const mobileAnalysisStyles = {
    background: 'transparent',
    border: 'none',
    borderRadius: '0',
    padding: '0',
    lineHeight: '1.6',
    fontSize: '1rem',
    color: '#495057',
    boxShadow: 'none',
    direction: 'rtl',
    width: '100%'
  };

  return (
    <div style={containerStyles}>
      <div style={isMobile ? mobileContentStyles : contentStyles}>
        <div style={isMobile ? mobileAnalysisStyles : analysisContentStyles}>
            {/* סיכום קצר */}
            {webhookData?.['תקציר_קצר'] && (
              <div style={{
                background: '#FFF9C4',
                border: '1px solid #FDD835',
                borderRadius: '6px',
                padding: '12px 16px',
                marginBottom: '24px',
                direction: 'rtl',
                fontSize: '1rem',
                color: '#2c3e50',
                lineHeight: '1.6'
              }}>
                {webhookData['תקציר_קצר']}
              </div>
            )}

            {/* סיכום שורה אחת בראש */}
            {webhookData?.['נתונים_מרכזיים'] && (
              <div style={{
                background: '#FFF9C4',
                border: '1px solid #FDD835',
                borderRadius: '6px',
                padding: '12px 16px',
                marginBottom: '0',
                direction: 'rtl',
                textAlign: 'center',
                fontWeight: '700',
                fontSize: '1rem',
                color: '#2c3e50',
                lineHeight: '1.5'
              }}>
                <span style={{ marginLeft: '0.5rem' }}>📌</span>
                {webhookData['נתונים_מרכזיים']?.['סכום_מרכזי'] && (
                  <>יתרה נוכחית: <strong style={{ color: '#28a745' }}>{formatNumber('סכום_מרכזי', webhookData['נתונים_מרכזיים']['סכום_מרכזי'])}</strong></>
                )}
                {webhookData['נתונים_מרכזיים']?.['מסלול_או_תוכנית'] && (
                  <> • מסלול: <strong>{webhookData['נתונים_מרכזיים']['מסלול_או_תוכנית']}</strong></>
                )}
                {webhookData['נתונים_מרכזיים']?.['גוף_מנהל'] && (
                  <> • ספק: <strong>{webhookData['נתונים_מרכזיים']['גוף_מנהל']}</strong></>
                )}
              </div>
            )}

            {/* מספרים מרכזיים - רשימת נקודות */}
            {webhookData?.['נתונים_מרכזיים'] && (
              <div style={{
                background: '#ffffff',
                border: '1px solid #E0E0E0',
                borderRadius: '6px',
                padding: '16px',
                marginTop: '0',
                marginBottom: '24px',
                direction: 'rtl',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <h3 style={{
                  color: '#2c3e50',
                  fontSize: '1rem',
                  fontWeight: '700',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  📊 מספרים מרכזיים
                </h3>
                <ul style={{
                  listStyle: 'disc',
                  paddingRight: '20px',
                  margin: '0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  {webhookData['נתונים_מרכזיים']?.['גוף_מנהל'] && (
                    <li style={{
                      fontSize: '1rem',
                      color: '#495057',
                      lineHeight: '1.6',
                      listStyleType: 'disc'
                    }}>
                      <strong>גוף מנהל:</strong> {webhookData['נתונים_מרכזיים']['גוף_מנהל']}
                    </li>
                  )}
                  {webhookData['נתונים_מרכזיים']?.['סוג_המוצר'] && (
                    <li style={{
                      fontSize: '1rem',
                      color: '#495057',
                      lineHeight: '1.6',
                      listStyleType: 'disc'
                    }}>
                      <strong>סוג המוצר:</strong> {webhookData['נתונים_מרכזיים']['סוג_המוצר']}
                    </li>
                  )}
                  {webhookData['נתונים_מרכזיים']?.['מסלול_או_תוכנית'] && (
                    <li style={{
                      fontSize: '1rem',
                      color: '#495057',
                      lineHeight: '1.6',
                      listStyleType: 'disc'
                    }}>
                      <strong>מסלול/תוכנית:</strong> {webhookData['נתונים_מרכזיים']['מסלול_או_תוכנית']}
                    </li>
                  )}
                  {webhookData['נתונים_מרכזיים']?.['סכום_מרכזי'] && (
                    <li style={{
                      fontSize: '1rem',
                      color: '#495057',
                      lineHeight: '1.6',
                      listStyleType: 'disc'
                    }}>
                      <strong>סכום מרכזי:</strong> {formatNumber('סכום_מרכזי', webhookData['נתונים_מרכזיים']['סכום_מרכזי'])}
                    </li>
                  )}
                  {webhookData['נתונים_מרכזיים']?.['דמי_ניהול_באחוזים'] && (
                    <li style={{
                      fontSize: '1rem',
                      color: '#495057',
                      lineHeight: '1.6',
                      listStyleType: 'disc'
                    }}>
                      <strong>דמי ניהול (%):</strong> {formatNumber('דמי_ניהול_באחוזים', webhookData['נתונים_מרכזיים']['דמי_ניהול_באחוזים'])}
                    </li>
                  )}
                  {webhookData['נתונים_מרכזיים']?.['נתונים_נוספים'] && Object.keys(webhookData['נתונים_מרכזיים']['נתונים_נוספים'] || {}).length > 0 && (
                    Object.entries(webhookData['נתונים_מרכזיים']['נתונים_נוספים'] || {}).map(([key, value]) => {
                      if (value === null || value === undefined) return null;
                      return (
                        <li key={key} style={{
                          fontSize: '1rem',
                          color: '#495057',
                          lineHeight: '1.6',
                          listStyleType: 'disc'
                        }}>
                          <strong>{key}:</strong> {formatNumber(key, value)}
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            )}

            {/* ממצאים חשובים - רשימת נקודות */}
            {webhookData?.['ממצאים_חשובים'] && webhookData['ממצאים_חשובים'].length > 0 && (
              <div style={{
                background: '#FFEBEE',
                border: '1px solid #E57373',
                borderRadius: '6px',
                padding: '16px',
                marginTop: '0',
                marginBottom: '24px',
                direction: 'rtl',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <h3 style={{
                  color: '#c62828',
                  fontSize: '1rem',
                  fontWeight: '700',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  🔍 ממצאים חשובים
                </h3>
                <ul style={{
                  listStyle: 'disc',
                  paddingRight: '20px',
                  margin: '0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  {webhookData['ממצאים_חשובים'].map((finding, index) => (
                    <li key={index} style={{
                      fontSize: '1rem',
                      color: '#495057',
                      lineHeight: '1.6',
                      listStyleType: 'disc'
                    }}>
                      {finding}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* המלצות פשוטות - רשימת נקודות */}
            {webhookData?.['המלצות_פשוטות'] && webhookData['המלצות_פשוטות'].length > 0 && (
              <div style={{
                background: '#E3F2FD',
                border: '1px solid #64B5F6',
                borderRadius: '6px',
                padding: '16px',
                marginTop: '0',
                marginBottom: '0',
                direction: 'rtl',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <h3 style={{
                  color: '#1565C0',
                  fontSize: '1rem',
                  fontWeight: '700',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  💡 המלצות
                </h3>
                <ul style={{
                  listStyle: 'disc',
                  paddingRight: '20px',
                  margin: '0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  {webhookData['המלצות_פשוטות'].map((recommendation, index) => (
                    <li key={index} style={{
                      fontSize: '1rem',
                      color: '#495057',
                      lineHeight: '1.6',
                      listStyleType: 'disc'
                    }}>
                      {recommendation}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* הודעת fallback אם אין תוכן */}
            {webhookData && 
             !webhookData?.['תקציר_קצר'] && 
             !webhookData?.['נתונים_מרכזיים'] && 
             (!webhookData?.['ממצאים_חשובים'] || webhookData['ממצאים_חשובים'].length === 0) && 
             (!webhookData?.['המלצות_פשוטות'] || webhookData['המלצות_פשוטות'].length === 0) && (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: '#6c757d',
                direction: 'rtl'
              }}>
                <p style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                  הנתונים התקבלו בהצלחה, אך אין תוכן להצגה.
                </p>
                <p style={{ fontSize: '1rem', fontStyle: 'italic' }}>
                  נא לבדוק את פורמט התגובה מהשרת.
                </p>
              </div>
            )}
          </div>
      </div>
    </div>
  );
}

